import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FormGroup, FormSection, Label } from "../styles";
import { Badge, BotaoPequeno, CabecalhoDaSecao, Campo, Hint, Prefixo, Tip } from "./EditorChrome";
import { whatsappApi, type WhatsappNaFicha } from "../../../services/whatsapp";
import { formatarNumero, formatarParaCampo, linkWaMe, normalizarNumero, numerosDiferem } from "./whatsapp-no-google";

interface WhatsappNoGoogleProps {
  profileId: string;
}

export const WhatsappNoGoogle: React.FC<WhatsappNoGoogleProps> = ({ profileId }) => {
  const [dados, setDados] = useState<WhatsappNaFicha | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [numero, setNumero] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(null);
    whatsappApi
      .consultar(profileId)
      .then((r) => {
        if (!ativo) return;
        setDados(r);
        setNumero(formatarParaCampo(r.numeroGeoranking ?? r.numeroNoGoogle));
      })
      .catch(() => {
        if (ativo) setErro("Não foi possível ler o WhatsApp da ficha agora.");
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [profileId]);

  const valido = normalizarNumero(numero) !== null;
  const teste = linkWaMe(numero);

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    setSalvo(false);
    try {
      const r = await whatsappApi.salvar(profileId, numero);
      setDados(r);
      setNumero(formatarParaCampo(r.numeroGeoranking));
      setSalvo(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível gravar o WhatsApp no Google.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <FormSection style={{ maxWidth: "none" }} aria-label="WhatsApp no Google">
      <CabecalhoDaSecao
        titulo="WhatsApp no Google"
        apoio="O Google mostra o botão de WhatsApp quando a ficha tem este link. Salvar aqui grava direto na ficha."
        extra={
          carregando ? null : dados?.linkNoGoogle ? (
            <Badge $tom="verde">
              <span className="material-icons">check_circle</span>
              Botão ativo no Google
            </Badge>
          ) : (
            <Badge $tom="cinza">Sem botão no Google</Badge>
          )
        }
      />

      {!carregando && dados?.linkNoGoogle && (
        <Hint as="p" style={{ margin: "0 0 16px" }}>
          {"Link na ficha agora: "}
          <a href={dados.linkNoGoogle} target="_blank" rel="noreferrer">
            {dados.linkNoGoogle}
          </a>
          {dados.sincronizadoEm && ` · conferido em ${dayjs(dados.sincronizadoEm).format("DD/MM/YYYY HH:mm")}`}
        </Hint>
      )}

      {numerosDiferem(dados) && (
        <Tip $tom="aviso" role="alert" style={{ marginBottom: 16 }}>
          <span className="material-icons">warning</span>
          {`O número do GeoRanking (${formatarNumero(dados?.numeroGeoranking)}) é diferente do que está no Google (${formatarNumero(dados?.numeroNoGoogle)}). Salve para corrigir a ficha.`}
        </Tip>
      )}

      <FormGroup style={{ marginBottom: 0 }}>
        <Label htmlFor="whatsapp-no-google">
          <span className="material-icons">chat</span>
          Número do WhatsApp
        </Label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Campo style={{ flex: "1 1 220px" }}>
            <Prefixo>+55</Prefixo>
            <input
              id="whatsapp-no-google"
              type="tel"
              value={numero}
              onChange={(e) => {
                setNumero(e.target.value);
                setSalvo(false);
              }}
              placeholder="(62) 99664-0758"
              disabled={carregando}
            />
            {teste && (
              <a
                href={teste}
                target="_blank"
                rel="noreferrer"
                className="material-icons"
                aria-label="Testar conversa"
                style={{ fontSize: 18, color: "#1264C7", textDecoration: "none" }}
              >
                open_in_new
              </a>
            )}
          </Campo>
          <BotaoPequeno type="button" $variante="filled" onClick={salvar} disabled={!valido || salvando || carregando}>
            <span className="material-icons">cloud_upload</span>
            {salvando ? "Salvando..." : "Salvar no Google"}
          </BotaoPequeno>
        </div>
        {numero && !valido && <Hint role="status">Informe DDD + número, por exemplo (62) 99664-0758.</Hint>}
        {salvo && <Hint role="status">WhatsApp gravado na ficha do Google.</Hint>}
        {erro && (
          <Hint role="alert" style={{ color: "#B3261E" }}>
            {erro}
          </Hint>
        )}
      </FormGroup>
    </FormSection>
  );
};
